import { z } from 'zod';

import {
  V2ArchivePermission,
  archivePermissionSchema,
  pagedSchema,
} from './common';

/** 폴더 1건 (FolderResponse) — 파일 상세(N2)의 소속 폴더 · N4 소속 설정 응답 공용 */
export const folderResponseSchema = z.object({
  id: z.number(),
  koreanName: z.string(),
  englishName: z.string(),
  level: z.number(),
  idPath: z.string(),
  koreanFullPath: z.string(),
  englishFullPath: z.string(),
  isPermissionUnique: z.boolean(),
  parentFolderId: z.number().nullable(),
  /** 조회 권한 여부 — top을 includeInaccessible=true로 받을 때만 false가 온다. 없으면(구 서버) true */
  accessible: z
    .boolean()
    .nullish()
    .transform((v) => v ?? true),
  isDeleted: z.boolean(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FolderResponse = z.infer<typeof folderResponseSchema>;

// #region 폴더 트리 — GET /folders/top · /folders/{id}/children · /folders/{id}/expand
/**
 * GET /folders/top — 조회 권한 있는 최상위 폴더 (FolderResponse[] · permission 필터 없음).
 * includeInaccessible=true면 권한 없는 최상위도 accessible=false로 함께 온다(자물쇠 표시용)
 */
export const folderTopListSchema = z.array(folderResponseSchema);

/**
 * 트리 노드 1건 (FolderTreeResponse) — children·expand 공용 응답.
 * 둘 다 **요청한 폴더 노드 하나**를 주며, children은 subFolderList에 자식 한 단계,
 * expand는 대상 폴더까지의 경로가 subFolderList로 중첩돼 온다.
 */
export type FolderTreeNode = {
  id: number;
  koreanName: string;
  englishName: string;
  level: number;
  idPath: string;
  isPermissionUnique: boolean;
  parentFolderId: number | null;
  /** 하위 폴더 존재 여부 — 확장 화살표 노출 판단 (권한 없는 폴더는 서버가 숨겨 false) */
  hasSubFolder: boolean;
  /**
   * 요청한 권한이 있는지 — includeInaccessible=true로 받을 때만 false가 온다(트리 자물쇠 표시).
   * 값이 없으면(구 서버) 접근 가능으로 본다
   */
  accessible: boolean;
  /** children 응답에서는 보통 빈 배열, expand 응답에서는 경로를 따라 채워진다 */
  subFolderList: FolderTreeNode[];
};

/** 재귀 스키마 — z.lazy는 타입을 추론하지 못하므로 위 타입을 명시한다 */
export const folderTreeNodeSchema: z.ZodType<
  FolderTreeNode,
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
  z.object({
    id: z.number(),
    koreanName: z.string(),
    englishName: z.string(),
    level: z.number(),
    idPath: z.string(),
    isPermissionUnique: z.boolean(),
    parentFolderId: z
      .number()
      .nullish()
      .transform((v) => v ?? null),
    hasSubFolder: z.boolean(),
    accessible: z
      .boolean()
      .nullish()
      .transform((v) => v ?? true),
    subFolderList: z
      .array(folderTreeNodeSchema)
      .nullish()
      .transform((v) => v ?? []),
  }),
);

/**
 * GET /folders/{id}/children 응답 → 자식 노드 배열.
 * 실제 서버는 자식 배열을 주고(ARC-23), 스웨거는 요청 노드 1건({subFolderList})이라
 * 두 형식을 모두 받아 자식 배열로 맞춘다 — 계약이 하나로 확정되면 한쪽을 지운다.
 */
export const folderChildrenResponseSchema = z.union([
  z.array(folderTreeNodeSchema),
  folderTreeNodeSchema.transform((node) => node.subFolderList),
]);

/**
 * children 조회 쿼리. permission은 **그 권한 이상인 폴더만** 서버가 걸러 준다
 * (스웨거 기본값 Read) — 좌측 트리는 Download, 저장 폴더/이동 목적지 피커는
 * Upload로 조회한다(규칙 #5). top·expand에는 permission 파라미터가 없다(R12).
 */
export type GetFolderChildrenParams = {
  topFolderId: number;
  permission?: V2ArchivePermission;
  /** true면 권한 없는 자식도 accessible=false로 함께 온다(이름만 · 하위 구조 숨김) — 좌측 트리 자물쇠용 */
  includeInaccessible?: boolean;
};
// #endregion

// #region N12 — POST /folders/{id}/access-request
export const accessRequestBodySchema = z.object({
  /** 요청할 권한 등급 — UI에서 선택받아 보낸다 */
  requestedPermission: archivePermissionSchema,
  message: z.string().nullish(),
});
export type AccessRequestBody = z.infer<typeof accessRequestBodySchema>;

export const accessRequestResponseSchema = z.object({
  id: z.number(),
  folderId: z.number(),
  requestedPermission: archivePermissionSchema,
  status: z.enum(['Pending', 'Approved', 'Rejected']),
  createdAt: z.string(),
  /** true면 기존 요청이 이미 있음 (중복 요청) */
  alreadyRequested: z.boolean(),
});
export type AccessRequestResponse = z.infer<typeof accessRequestResponseSchema>;
// #endregion

// #region E1 — GET /folders/{id}/content 페이지네이션
/** 하위 폴더 1건 (SubFolderResponse) = FolderResponse + 하위 개수 */
export const subFolderResponseSchema = folderResponseSchema.extend({
  subFolderCount: z.number(),
  subFileCount: z.number(),
});
export const v2FolderContentPageSchema = pagedSchema(subFolderResponseSchema);
export type SubFolderResponse = z.infer<typeof subFolderResponseSchema>;
export type V2FolderContentPage = z.infer<typeof v2FolderContentPageSchema>;

export type GetFolderContentPagedParams = {
  topFolderId?: number;
  pageNumber: number;
  pageSize: number;
};
// #endregion
